import { defineComponent, createLibrary } from '@customerio/hydra-copilot/react';
import type { PromptOptions } from '@customerio/hydra-copilot/react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardSection,
  Grid,
  InlineStack,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { open as openSidebar } from '@/features/sidebar';
import { isInternalLink, isSafeUrl, isSameWorkspaceLink } from '@/utils/url';
import React, { Suspense, useMemo } from 'react';
import type { ComponentProps } from 'react';
import { z } from '@customerio/hydra-validation';
import { Chart } from '@/components/data-display/highcharts';
import { AppErrorBoundary } from '@/components/app-error-boundary';

// Register Series as a named type so the prompt shows `Series[]`
// instead of the verbose inline `{name: string, data: number[]}[]`.
const SeriesSchema = z.object({
  name: z.string(),
  data: z.array(z.number()),
});
// Zod 4 throws if the same id is registered twice. This can happen on HMR or
// test re-evaluation because each re-execution creates a new SeriesSchema
// instance, so `get(SeriesSchema)` returns null even if 'Series' is already
// claimed by the previous instance. A try/catch is the safest guard.
try {
  z.globalRegistry.add(SeriesSchema, { id: 'Series' });
} catch {
  // 'Series' id already registered from a previous module evaluation — safe to ignore.
}

const LinkButtonDef = defineComponent({
  name: 'LinkButton',
  description:
    'A button that navigates to a URL. Use at the end of a response to link the user to a relevant page (e.g. "View Transactional", "Go to Campaign").',
  props: z.object({
    label: z.string(),
    href: z.string(),
  }),
  component: ({ props }) => {
    const internal = isInternalLink(props.href);
    if (internal != null && isSameWorkspaceLink(props.href)) {
      return (
        <AppErrorBoundary>
          <InlineStack>
            <Button
              href={internal.relativePath}
              variant="primary"
              size="sm"
              icon="chevron-right"
              iconPosition="trailing"
              onClick={(event_) => {
                if (!event_.metaKey && !event_.ctrlKey) openSidebar('copilot');
              }}
            >
              {props.label}
            </Button>
          </InlineStack>
        </AppErrorBoundary>
      );
    }
    const safeHref = isSafeUrl(props.href) ? props.href : undefined;

    return (
      <AppErrorBoundary>
        <InlineStack>
          <Button href={safeHref} variant="secondary" size="sm" icon="exit" iconPosition="trailing" isExternal>
            {props.label}
          </Button>
        </InlineStack>
      </AppErrorBoundary>
    );
  },
});

// ─── Chart ───────────────────────────────────────────────────────────────────

const SankeyLinkSchema = z.object({
  from: z.string(),
  to: z.string(),
  weight: z.number(),
});

try {
  z.globalRegistry.add(SankeyLinkSchema, { id: 'SankeyLink' });
} catch {
  // Already registered — safe to ignore on HMR.
}

const CHART_TYPES = ['line', 'bar', 'column', 'pie', 'area', 'spline', 'sankey'] as const;
type GridGap = NonNullable<ComponentProps<typeof Grid>['gap']>;

type ChartProps = {
  series?: z.infer<typeof SeriesSchema>[];
  type?: (typeof CHART_TYPES)[number];
  categories?: string[];
  links?: z.infer<typeof SankeyLinkSchema>[];
};

// Lazy-loaded sankey renderer — loads the Highcharts sankey module on first
// mount (needs core to be ready first), then renders via <Chart>.
const LazySankeyRenderer = React.lazy(async () => {
  await import('@/components/data-display/highcharts/chart');
  await import('highcharts/modules/sankey');

  type SankeyInnerProps = { links: z.infer<typeof SankeyLinkSchema>[] };

  function SankeyRenderer({ links }: SankeyInnerProps) {
    const options = useMemo(
      () => ({
        chart: { height: 400 },
        plotOptions: {
          sankey: {
            linkOpacity: 0.4,
            dataLabels: { style: { fontSize: '13px', fontWeight: '600' } },
            states: { hover: { linkOpacity: 0.7 } },
          },
        },
        series: [
          {
            type: 'sankey' as const,
            keys: ['from', 'to', 'weight'],
            data: links.map((l) => [l.from, l.to, l.weight]),
          },
        ],
        tooltip: {
          nodeFormat: '{point.name}: <b>{point.sum}</b><br/>',
          pointFormat: '{point.fromNode.name} → {point.toNode.name}: <b>{point.weight}</b><br/>',
        },
      }),
      [links],
    );
    return <Chart options={options} />;
  }

  return { default: SankeyRenderer };
});

function CartesianChartRenderer({ series, type, categories }: ChartProps) {
  // Stable options reference prevents Highcharts re-initializing on every
  // streaming token when the props haven't actually changed.
  const options = useMemo(() => {
    const resolvedType = type ?? 'line';
    const resolvedSeries = series ?? [];
    return {
      chart: { height: 300 },
      ...(resolvedType !== 'pie' && categories != null ? { xAxis: { categories } } : {}),
      series: resolvedSeries.map((s) => ({
        type: resolvedType,
        name: s.name,
        data:
          resolvedType === 'pie' && categories != null
            ? categories.map((cat, index) => ({ name: cat, y: s.data[index] ?? 0 }))
            : s.data,
      })),
    };
  }, [series, type, categories]);
  return <Chart options={options} />;
}

function ChartRenderer({ series, type, categories, links }: ChartProps) {
  if (type === 'sankey') {
    if (links == null) return null;
    return (
      <Suspense fallback={<div style={{ height: 400 }} />}>
        <LazySankeyRenderer links={links} />
      </Suspense>
    );
  }
  return <CartesianChartRenderer series={series} type={type} categories={categories} />;
}

const ChartDef = defineComponent({
  name: 'Chart',
  description: 'Renders a Highcharts data visualization. Use type="sankey" with links for flow diagrams.',
  props: z.object({
    series: z.array(SeriesSchema).optional(),
    type: z.enum(CHART_TYPES).optional(),
    categories: z.array(z.string()).optional(),
    links: z.array(SankeyLinkSchema).optional(),
  }),
  component: ({ props }) => (
    <AppErrorBoundary>
      <ChartRenderer series={props.series} type={props.type} categories={props.categories} links={props.links} />
    </AppErrorBoundary>
  ),
});

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCardDef = defineComponent({
  name: 'StatCard',
  description: 'Displays a single metric label and value.',
  props: z.object({
    title: z.string(),
    value: z.string(),
  }),
  component: ({ props }) => (
    <AppErrorBoundary>
      <Card>
        <CardHeader>
          <CardTitle>{props.title}</CardTitle>
        </CardHeader>
        <CardSection>
          <Text size="xl" weight="semibold">
            {props.value}
          </Text>
        </CardSection>
      </Card>
    </AppErrorBoundary>
  ),
});

// ─── Card ─────────────────────────────────────────────────────────────────────

const CardDef = defineComponent({
  name: 'Card',
  description: 'A card container with an optional title. Use to wrap a Chart.',
  props: z.object({
    children: z.array(z.union([ChartDef.ref, StatCardDef.ref])),
    title: z.string().optional(),
  }),
  component: ({ props, renderNode }) => (
    <AppErrorBoundary>
      <Card>
        {props.title != null ? (
          <CardHeader>
            <CardTitle>{props.title}</CardTitle>
          </CardHeader>
        ) : null}
        <CardSection>{renderNode(props.children)}</CardSection>
      </Card>
    </AppErrorBoundary>
  ),
});

// ─── Grid ─────────────────────────────────────────────────────────────────────

const GridDef = defineComponent({
  name: 'Grid',
  description: 'Arranges cards in a multi-column layout.',
  props: z.object({
    children: z.array(z.union([StatCardDef.ref, CardDef.ref])),
    columns: z.number().optional(),
    gap: z.enum(['100', '200', '300', '400']).optional(),
  }),
  component: ({ props, renderNode }) => {
    const gap: GridGap = props.gap ?? '200';

    return (
      <AppErrorBoundary>
        <Grid columns={props.columns ?? 2} gap={gap}>
          {renderNode(props.children)}
        </Grid>
      </AppErrorBoundary>
    );
  },
});

// ─── Root ─────────────────────────────────────────────────────────────────────

const RootDef = defineComponent({
  name: 'Root',
  description: 'Top-level container. Every response must assign root = Root(...).',
  props: z.object({
    children: z.array(z.union([StatCardDef.ref, CardDef.ref, GridDef.ref, LinkButtonDef.ref])),
  }),
  component: ({ props, renderNode }) => (
    <AppErrorBoundary>
      <Stack gap="200">{renderNode(props.children)}</Stack>
    </AppErrorBoundary>
  ),
});

// ─── Library ──────────────────────────────────────────────────────────────────

export const copilotLibrary = createLibrary({
  root: 'Root',
  components: [LinkButtonDef, ChartDef, StatCardDef, CardDef, GridDef, RootDef],
  componentGroups: [
    {
      name: 'Navigation',
      components: ['LinkButton'],
      notes: [
        '- Use LinkButton at the end of a response to give the user a clear call-to-action to navigate somewhere relevant.',
        '- Examples: "View Transactional", "Go to Campaign", "Open Settings".',
        '- For in-platform pages, use the full URL path (e.g. href="/transactional/123").',
        '- Prefer a single LinkButton per response. Use multiple only when there are distinct next steps.',
      ],
    },
    {
      name: 'Layout',
      components: ['Root', 'Grid'],
      notes: [
        '- Always write root = Root([...]) as the very first statement.',
        '- Grid defaults to 2 columns. Use columns=3 for stat overviews.',
      ],
    },
    {
      name: 'Cards',
      components: ['StatCard', 'Card'],
      notes: [
        '- StatCard(title, value): for a single metric; value is a pre-formatted string e.g. "1,234" or "$42K".',
        '- Card([chart], title?): use to wrap a Chart with an optional title.',
        '- Do NOT use StatCard or Card to display lists of items (e.g. campaigns, segments, broadcasts). Use plain markdown (bullet lists, numbered lists, or tables) for listing items.',
        '- Only use StatCard for numeric metrics/KPIs. Only use Card to wrap a Chart.',
      ],
    },
    {
      name: 'Charts',
      components: ['Chart'],
      notes: [
        '- Use "line" or "area" for trends over time, "column" or "bar" for comparisons, "pie" for proportions, "sankey" for flow diagrams.',
        '- For non-sankey types: series is required, data is always number[], categories provides the labels (x-axis for cartesian, slice names for pie).',
        '- categories length must match data length in every series.',
        '- Pie: pass exactly one series; categories become the slice labels.',
        '- Sankey: use type="sankey" with links instead of series. Each link has from (source node name), to (target node name), weight (numeric flow value). Node names are inferred from links. Omit series and categories.',
        '- Chart has no title — wrap it in Card to add a title.',
      ],
    },
  ],
});

export const copilotPromptOptions: Omit<PromptOptions, 'preamble'> = {
  examples: [
    // Stat grid
    `root = Root([grid])
grid = Grid([c1, c2, c3], 3)
c1 = StatCard("Total Users", "12,847")
c2 = StatCard("Active", "9,203")
c3 = StatCard("Churn Rate", "2.4%")`,

    // Single chart in a card
    `root = Root([card])
card = Card([chart], "Monthly Signups")
chart = Chart([{name:"Signups",data:[120,145,132,178]}], "line", ["Jan","Feb","Mar","Apr"])`,

    // Chart + stat grid
    `root = Root([grid, card])
grid = Grid([c1, c2], 2)
c1 = StatCard("Total Revenue", "$148K")
c2 = StatCard("Growth", "+18%")
card = Card([chart], "Revenue Trend")
chart = Chart([{name:"Revenue",data:[42,51,48,63,55,70]}], "area", ["Jan","Feb","Mar","Apr","May","Jun"])`,

    // Pie chart — one series, categories become slice labels
    `root = Root([card])
card = Card([chart], "Messages by Channel")
chart = Chart([{name:"Channels",data:[38,22,18,14,8]}], "pie", ["Email","Push","SMS","In-App","Webhook"])`,

    // Sankey — user flow breakdown
    `root = Root([card])
card = Card([chart], "Message Delivery Flow")
chart = Chart([], "sankey", [], [{from:"Sent",to:"Delivered",weight:8500},{from:"Sent",to:"Bounced",weight:1500},{from:"Delivered",to:"Opened",weight:5200},{from:"Delivered",to:"Ignored",weight:3300},{from:"Opened",to:"Clicked",weight:2100},{from:"Opened",to:"No Click",weight:3100}])`,

    // LinkButton — navigate to a relevant page
    `root = Root([btn])
btn = LinkButton("View Transactional", "/transactional/123")`,

    // Stats with a link to dig deeper
    `root = Root([grid, btn])
grid = Grid([c1, c2], 2)
c1 = StatCard("Delivered", "12,847")
c2 = StatCard("Opened", "4,203")
btn = LinkButton("View Campaign", "/campaigns/456")`,
  ],
};
